import { useState } from "react";
import { Input } from "@medbillai/ui";
import { normalizeMemberId } from "@medbillai/utils/memberId";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { captureUnreportedError } from "../../lib/apollo/error";
import { type OnDone, tagged } from "../../lib/flow-engine";
import type { OnboardingIdentity } from "./types";
import { useRecordCarrier } from "./utils/useRecordCarrier";
export type MemberIdOutcomes = {
  submitted: {};
  /** The user doesn't have the ID to hand; the carrier is recorded without it. */
  skipped: {};
  back: {};
};
const Outcome = tagged<MemberIdOutcomes>();
const V3_NAV: OnboardingNavProps = {
  showLogo: false
};
export function MemberId({
  identity,
  nav = V3_NAV,
  carrier,
  payerId,
  onDone
}: {
  identity?: OnboardingIdentity;
  nav?: OnboardingNavProps;
  carrier: string;
  payerId: string;
  onDone: OnDone<MemberIdOutcomes>;
}) {
  const [typed, setTyped] = useState("");
  const [rejected, setRejected] = useState(false);
  // Blocks a second tap from landing a second record.
  const [answered, setAnswered] = useState(false);
  const recordCarrier = useRecordCarrier(identity);
  const record = (memberId?: string) => {
    setAnswered(true);
    void recordCarrier(carrier, payerId, memberId).catch(captureUnreportedError).finally(() => setAnswered(false));
  };
  const submit = () => {
    const memberId = normalizeMemberId(typed);
    if (memberId === null) {
      setRejected(true);
      return;
    }
    onDone(Outcome.submitted());
    record(memberId);
  };
  const skip = () => {
    onDone(Outcome.skipped());
    record();
  };
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="MemberId" data-sentry-source-file="MemberId.tsx">
      <OnboardingScreen.Nav {...nav} onBackPress={() => onDone(Outcome.back())} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="MemberId.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="MemberId.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your member ID?" subtitle="It's on the front of your insurance card. We use it to look up your plan." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="MemberId.tsx" />
        <Input testID="member-id.input" label="Member ID" value={typed} onChangeText={value => {
        setTyped(value);
        setRejected(false);
      }} autoFocus autoCapitalize="characters" autoComplete="off" autoCorrect={false} spellCheck={false} validation={rejected ? "error" : "none"} helperText={rejected ? "That doesn't look like a member ID. Check your card and try again." : undefined} data-sentry-element="Input" data-sentry-source-file="MemberId.tsx" />
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="MemberId.tsx">
        <OnboardingScreen.PrimaryAction testID="member-id.continue" variant="brand" disabled={typed.trim() === "" || answered} onPress={submit} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="MemberId.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="member-id.skip" disabled={answered} onPress={skip} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="MemberId.tsx">
          I don't have it
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}